import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from 'src/app/shared/rbac.service';
import { ApiDataSource } from 'src/app/utils/api-data-source';
import { SsspTabsDialogComponent } from '../sssp-tabs/sssp-tabs-dialog.component';
import { SsspFilterComponent } from '../sssp-filter/sssp-filter.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-sssp-list',
  templateUrl: './sssp-list.component.html',
  styleUrls: ['./sssp-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SsspListComponent implements OnInit {
  listColumns: string[] = [
    'select',
    'id',
    'site_name',
    'site_location',
    'contractor',
    'tags',
    'date_modified',
    // 'date_modified_UTC',
    'actions'
  ];
  listDataSource = new SSSPDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService,
    private router: Router
  ) {}

  protected readonly RBACService = RBACService;

  ngOnInit() {
    // Check permissions - user must have view permission
    if (!this.canViewSafetyPlans()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view safety plans.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // Set displayed columns based on permissions
    if (this.canDeleteSafetyPlans()) {
      this.listColumns = [
        'select',
        'id',
        'site_name',
        'site_location',
        'contractor',
        'tags',
        'date_modified',
        'actions'
      ];
    } else {
      this.listColumns = [
        'id',
        'site_name',
        'site_location',
        'contractor',
        'tags',
        'date_modified',
        'actions'
      ];
    }

    this.listDataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.listDataSource.limit = paginator['pageSize'];
      this.listDataSource.offset = paginator['pageIndex'];
      this.listDataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange
      .pipe(
        tap((sorter) => {
          this.listDataSource.order = sorter['direction'];
          // sorting for utc time by parsing original time
          if (sorter['active'] === 'date_modified_UTC') {
            this.listDataSource.order_by = 'date_modified';
          } else {
            this.listDataSource.order_by = sorter['active'];
          }

          this.listDataSource.getData();
        })
      )
      .subscribe();
  }

  onManageSSSP(sssp_hash: string) {
    // Check permission before opening dialog
    if (!this.canEditSafetyPlans()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit safety plans.'
      );
      return;
    }

    if (!sssp_hash) {
      return;
    }

    this.utils
      .showComponentDialog(
        SsspTabsDialogComponent,
        {
          sssp_hash: sssp_hash
        },
        {
          width: '1200px'
        }
      )
      .then(() => {});
  }

  onRemove(id: number, evt: any) {
    // Check permission before showing confirmation
    if (!this.canDeleteSafetyPlans()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete safety plans.'
      );
      return;
    }

    const safety_plan_term = this.utils.getLangTerm(
      'safety-plans.singular',
      'Safety Plan'
    );
    this.utils.showQuickActions(
      evt.target,
      `Are you sure you want to remove this ${safety_plan_term}?`,
      [
        {
          text: 'Yes',
          handler: () => {
            this.api
              .makeRequest('delete', `v1/sssp/${id}`)
              .then(() => {
                this.listDataSource.getData();
                const safety_plan_term = this.utils.getLangTerm(
                  'safety-plans.singular',
                  'Safety Plan'
                );
                this.utils.showToast(`The ${safety_plan_term} was removed.`);
              })
              .catch((error) => {
                this.utils.handleAPIErrors(error);
              });
          }
        },
        {
          text: 'No',
          handler: () => {}
        }
      ]
    );
  }

  // Remove multiple selected messages.
  onRemoveSelected() {
    // Check permission before showing confirmation
    if (!this.canDeleteSafetyPlans()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete safety plans.'
      );
      return;
    }

    const safety_plan_term = this.utils.getLangTerm(
      'safety-plans.singular',
      'Safety Plan'
    );
    this.utils.showModal(
      `Remove Selected ${safety_plan_term}(s)`,
      `Are you sure you want to remove the selected ${safety_plan_term}(s)?`,
      () => {
        const ssspIds = this.listDataSource.selection.selected.join(',');
        this.api
          .makeRequest('delete', `v1/sssp/${ssspIds}`)
          .then(() => {
            this.listDataSource.selection.clear();
            this.listDataSource.getData();
            const safety_plan_term = this.utils.getLangTerm(
              'safety-plans.singular',
              'Safety Plan'
            );
            this.utils.showToast(
              `The selected ${safety_plan_term}(s) was removed.`
            );
          })
          .catch((error) => {
            this.utils.handleAPIErrors(error);
          });
      }
    );
  }

  onFilter() {
    this.utils.showComponentDialog(
      SsspFilterComponent,
      {
        selected_site_ids: this.listDataSource.site_ids,
        selected_contractor_ids: this.listDataSource.contractor_ids
      },
      {
        width: '500px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.listDataSource.site_ids = results.site_ids
            ? results.site_ids
            : this.listDataSource.site_ids;
          this.listDataSource.contractor_ids = results.contractor_ids
            ? results.contractor_ids
            : this.listDataSource.contractor_ids;
          this.listDataSource.getData();
        }
      }
    );
  }

  /**
   * Check if user can view account-level safety plans
   */
  canViewSafetyPlans(): boolean {
    return this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SAFETY_PLANS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
  }

  /**
   * Check if user can edit account-level safety plans
   */
  canEditSafetyPlans(): boolean {
    return this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SAFETY_PLANS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
  }

  /**
   * Check if user can delete account-level safety plans
   */
  canDeleteSafetyPlans(): boolean {
    return this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SAFETY_PLANS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );
  }

  /**
   * Check if user can use the filter (needs sites or contractors view permission)
   */
  canUseFilter(): boolean {
    return this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
  }
}

export class SSSPDataSource extends ApiDataSource {
  order_by = 'date_created';
  order = 'desc';

  contractor_ids: number[] = [];
  site_ids: number[] = [];

  getData(resetOffset: boolean = false) {
    this.makeRequest('v1/sssp', resetOffset, {
      contractor_ids: this.contractor_ids.length
        ? this.contractor_ids.join(',')
        : '',
      site_ids: this.site_ids.length ? this.site_ids.join(',') : ''
    });
  }
}
